import { useEffect, useState } from 'react';
import axios from 'axios';
import PropertyCard from '../components/PropertyCard';
import SEO from '../components/SEO';
import { getApiEndpoint, getAppUrl } from '../config/api';
import loadingImg from '../assets/loading.png';
import emptyStateImg from '../assets/empty-state.png';

const Listings = () => {
    const [properties, setProperties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterType, setFilterType] = useState('all'); // 'all', 'PG', 'Flat'

    useEffect(() => {
        const fetchProperties = async () => {
            try {
                const response = await axios.get(getApiEndpoint('get_properties.php'));
                setProperties(response.data);
                setLoading(false);
            } catch (err) {
                console.error("Error fetching properties:", err);
                setError("Failed to load properties. Please try again later.");
                setLoading(false);
            }
        };

        fetchProperties();
    }, []);

    // Filter properties based on selected type
    const filteredProperties = properties.filter(property => {
        if (filterType === 'all') return true;
        return property.type === filterType;
    });

    // Count properties by type
    const counts = {
        all: properties.length,
        PG: properties.filter(p => p.type === 'PG').length,
        Flat: properties.filter(p => p.type === 'Flat').length
    };

    if (loading) return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 'calc(100vh - 200px)',
            padding: '2rem'
        }}>
            <img
                src={loadingImg}
                alt="Loading"
                style={{ width: '150px', height: '150px', marginBottom: '1rem', animation: 'pulse 2s ease-in-out infinite' }}
            />
            <p style={{ fontSize: '1.25rem', color: 'var(--neutral-600)', fontWeight: '500' }}>Loading properties...</p>
        </div>
    );
    if (error) return <div style={{ textAlign: 'center', padding: '2rem', color: 'red' }}>{error}</div>;

    return (
        <div style={{ paddingTop: '100px', paddingBottom: '4rem' }}>
            <SEO
                title="Property Listings - PG & Flats in Pune | Hashtag"
                description="Browse all available PG accommodations and flats for rent in Pune. Filter by PG or Flat type. Premium amenities and great locations."
                keywords="PG listings Pune, flats for rent, available properties, rental listings Pune"
                url={getAppUrl('listings')}
            />
            <div className="container">
                <h1 className="section-title" style={{ marginBottom: '2rem' }}>Available Properties</h1>

                {/* Filter Toggle Buttons */}
                <div className="filter-button-container" style={{
                    display: 'flex',
                    gap: '1rem',
                    marginBottom: '3rem',
                    flexWrap: 'wrap'
                }}>
                    <button
                        onClick={() => setFilterType('all')}
                        style={{
                            padding: '0.75rem 2rem',
                            borderRadius: '0.5rem',
                            border: filterType === 'all' ? 'none' : '1px solid #e2e8f0',
                            backgroundColor: filterType === 'all' ? 'var(--primary-500)' : 'white',
                            color: filterType === 'all' ? 'white' : 'var(--text-dark)',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            fontSize: '1rem'
                        }}
                    >
                        All ({counts.all})
                    </button>
                    <button
                        onClick={() => setFilterType('PG')}
                        style={{
                            padding: '0.75rem 2rem',
                            borderRadius: '0.5rem',
                            border: filterType === 'PG' ? 'none' : '1px solid #e2e8f0',
                            backgroundColor: filterType === 'PG' ? 'var(--primary-500)' : 'white',
                            color: filterType === 'PG' ? 'white' : 'var(--text-dark)',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            fontSize: '1rem'
                        }}
                    >
                        PG ({counts.PG})
                    </button>
                    <button
                        onClick={() => setFilterType('Flat')}
                        style={{
                            padding: '0.75rem 2rem',
                            borderRadius: '0.5rem',
                            border: filterType === 'Flat' ? 'none' : '1px solid #e2e8f0',
                            backgroundColor: filterType === 'Flat' ? 'var(--primary-500)' : 'white',
                            color: filterType === 'Flat' ? 'white' : 'var(--text-dark)',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            fontSize: '1rem'
                        }}
                    >
                        Flat ({counts.Flat})
                    </button>
                </div>

                {/* Properties Grid */}
                {filteredProperties.length > 0 ? (
                    <div className="grid">
                        {filteredProperties.map(property => (
                            <PropertyCard key={property.id} property={property} />
                        ))}
                    </div>
                ) : (
                    <div style={{
                        textAlign: 'center',
                        padding: '4rem 2rem',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <img
                            src={emptyStateImg}
                            alt="No properties found"
                            style={{ width: '200px', height: '200px', marginBottom: '2rem', opacity: 0.8 }}
                        />
                        <p style={{ fontSize: '1.5rem', marginBottom: '0.5rem', color: 'var(--neutral-700)', fontWeight: '600' }}>No properties found</p>
                        <p style={{ color: 'var(--neutral-500)', fontSize: '1rem' }}>Try selecting a different filter</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Listings;
